import { useEffect, useRef } from "react";
import type { FrameTarget } from "@archlang/scene/board";
import { ringTargets, type RingContext } from "./scopeRings";

/**
 * Frame-now — **⌃⌥⏎** frames the current attention anchor (the caret's node,
 * else the board selection) at the current altitude ring, RIGHT NOW. The manual
 * trigger of what auto-frame does after ~1.5s of sustained editing: "look here."
 *
 * It routes through the same `frameAtRing` verb as the scope dial, so it works
 * whether or not smart-view autopilot is toggled on (the explicit "show me"
 * gesture), and — like the dial — it drops any pending drift-back (this IS where
 * you want the camera).
 *
 * `e.code`-matched + capture phase (survives editor focus); ⌃⌥⏎ isn't a Monaco
 * default on any platform, so it's cross-platform (unlike the ⌃⌥↑/↓ dial).
 */
export interface FrameNowOpts {
  readonly active: boolean;
  /** The anchor to frame — caret node, else board selection. */
  readonly anchorId: () => string | null;
  readonly neighbors: (id: string) => readonly string[];
  readonly focalPathOf: (id: string) => readonly string[] | null;
  /** Current altitude ring (frame at this radius, don't change it). */
  readonly ring: number;
  readonly frameAtRing: (target: FrameTarget) => void;
}

export function useFrameNow(opts: FrameNowOpts): void {
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    if (!opts.active) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.code !== "Enter" || !e.ctrlKey || !e.altKey || e.metaKey || e.shiftKey) return;
      const o = optsRef.current;
      const anchor = o.anchorId();
      if (!anchor) return;
      const ctx: RingContext = { neighbors: o.neighbors, focalPathOf: o.focalPathOf };
      const target = ringTargets(o.ring, anchor, ctx) ?? { ids: [anchor] };
      e.preventDefault();
      e.stopPropagation();
      o.frameAtRing(target);
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.active]);
}
